/* eslint-disable @typescript-eslint/no-explicit-any */

// Imports for external libraries go here.
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { SearchResultsFilterPanelProps } from './SearchResultsFilterPanel.types';
import { StyledSearchResultsFilterPanel } from './SearchResultsFilterPanel.styles';
import { CheckboxControlsList, DropDownModal, Modal } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { BrandFilter } from '../BrandFilter/BrandFilter';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import {
  excludeRegionBucketsFromLocationFacets,
  fetchHDData,
  filterAvailableFilters,
  getFilteredOrder,
  mapActivitiesToBrandsStructure,
  updateFiltersPayload,
  updateURLParams,
} from '../../modules/helpers';
import { useApolloClient } from '@apollo/client';
import { phoenixLuxurySearchByGeoQueryMapFilters, phoenixLuxurySearchByLocationMapFilters } from '../MapView/queries';
import {
  DEFAULT_FILTERS_ORDER,
  FILTERS_MAPPING,
  LOCATION,
  MOBILE_COL_LENGTH,
  COL_LENGTH,
  QUERY_PARAM_MAPPING,
} from '../../modules/constants/constant';
import { DropDownComponent } from '../DropDownComponent';

const REGIONS_WITH_COUNTRIES_FIRST = new Set([
  'africa',
  'asia',
  'australia-and-the-pacific-islands',
  'caribbean',
  'central-america',
  'europe',
  'middle-east',
  'south-america',
]);

const REGIONS_WITH_STATES_FIRST = new Set(['united-states', 'canada', 'mexico']);

// Use named rather than default exports.
export const SearchResultsFilterPanel: FC<SearchResultsFilterPanelProps> = ({
  /**
   * get's AEM label's...
   */
  filterProps,

  /**
   * filter's response to display on UI
   */
  facets,

  /**
   * payload of the UXL query....
   */
  filtersPayload,
  setFiltersPayload,

  /**
   * Popup state
   */
  popupOpenState,
  /**
   * to change the popup state
   */
  setPopupOpenState,

  /**
   * To track weather user manually clicked the brand filter's or not
   */
  brandsClicked,
  setBrandsClicked,

  defaultRegion,

  /**
   * To track which filter pill user has clicked ....
   */
  filterButtonClicked,
  setFilterButtonClicked,

  /**
   * get's the exact viewPort
   */
  viewPort,

  /**
   * is true for the lat nd lng search (Search here btn use case) ....
   */
  isGeoQuerySearch,
  /**
   * To track weather user performed the search using search here btn
   * and which in turn used to trigger respective UXL call(either location search or geo search)....
   */
  setIsGeoQuerySearch,

  /**
   * Get's the center of the map when user change's map postion and used to trigger the Geo Query search
   */
  mapCenter,

  isAuthorModeEnabled,
}) => {
  const client = useApolloClient();

  const clientEnvVars = useClientEnvVarsStore(state => state.envVarsObject);

  const { ACCEPT_LANGUAGE: currentLocale, REQUEST_ID: requestID } = clientEnvVars;

  //REF's here ....
  const prevfiltersRef = useRef<any>([]);
  const filtersRef = useRef<any>();
  const filterPanelRef = useRef<any>(null);
  const dropdownRef = useRef<any>(null);

  filtersRef.current = filtersPayload;
  const prevfiltersRefVal = filtersRef.current;

  //State's Here....
  const [defaultSelectedFilters, setDefaultSelectedFilters] = useState<any>(filtersPayload);
  const [isLocationSearch, setIsLocationSearch] = useState<any>(false);

  /**
   * To set the updated filter's res. when user interact's with the filter's in the Popup
   */
  const [filtersFacets, setFiltersFacets] = useState<any>([]);

  /**
   * to show the region value in the region input field...
   */
  const [regionValue, setRegionValue] = useState<any>(
    defaultSelectedFilters?.[defaultSelectedFilters?.length - 1]?.dimensions[0]
  );

  /**
   * state to handle the brands click when user clicked on done btn ....
   */
  const [isTempBrandsClicked, setIsTempBrandsClicked] = useState<boolean>(() => brandsClicked);

  useEffect(() => {
    prevfiltersRef.current = filtersPayload;

    setFiltersFacets(facets);
  }, [popupOpenState]);

  const getData = async (payload: any, resetFilters = false) => {
    const locQueryvariables = {
      search: {
        options: { numberInParty: 1, countsOnly: true },
        facets: {
          terms: [...payload],
        },
      },
      limit: 10,
      offset: 0,
    };

    const distance = Number(filterProps?.searchRadius);

    const geoQueryVariables = {
      search: {
        latitude: mapCenter?.lat,
        longitude: mapCenter?.lng,
        distance: distance,
        distanceUnit: 'MILES',
        options: {
          numberInParty: 1,
        },
        facets: {
          terms: [...payload],
        },
      },
      limit: 10,
      offset: 0,
    };

    const variables = isGeoQuerySearch && !resetFilters ? geoQueryVariables : locQueryvariables;

    const query =
      isGeoQuerySearch && !resetFilters
        ? phoenixLuxurySearchByGeoQueryMapFilters
        : phoenixLuxurySearchByLocationMapFilters;

    const data = await fetchHDData(client, query, {
      variables: variables,
      context: {
        headers: {
          'x-request-id': requestID,
          'accept-language': currentLocale?.replace('_', '-'),
        },
      },
    });

    const searchResultsFacets =
      data?.search?.properties?.searchByLocation?.facets ?? data?.search?.properties?.searchByGeolocation?.facets ?? [];

    data?.search?.properties?.searchByLocation ? setIsLocationSearch(true) : setIsLocationSearch(false);

    setFiltersFacets(searchResultsFacets);
  };

  // update payload on selection of any filters
  const handleFiltersSelectionChange = (code: string, filterObjList: any) => {
    let temp: any = [];
    if (code === FILTERS_MAPPING?.Regions) {
      temp?.push(filterObjList?.code);
    } else if (code === FILTERS_MAPPING?.City || code === FILTERS_MAPPING?.State || code === FILTERS_MAPPING?.Country) {
      temp?.push(filterObjList?.code);
    } else if (code === FILTERS_MAPPING?.Brands) {
      filterObjList?.length > 0
        ? filterObjList?.forEach((obj: any) => {
            temp?.push(obj?.brandTagId);
          })
        : temp.push(...(filterProps?.brandTags || []));

      temp = [...new Set(temp)];

      filterObjList?.length > 0 && filterProps?.brandTags && filterProps?.brandTags?.length > 1
        ? setBrandsClicked(true)
        : setBrandsClicked(false);
    } else if (code === FILTERS_MAPPING?.Activities) {
      filterObjList?.forEach((obj: any) => {
        temp?.push(obj?.brandTagId);
      });
      temp = [...new Set(temp)];
    } else {
      filterObjList?.forEach((obj: any) => temp?.push(obj?.code));
    }

    let updatedArray;
    if (code === FILTERS_MAPPING?.City || code === FILTERS_MAPPING?.State || code === FILTERS_MAPPING?.Country) {
      updatedArray = defaultSelectedFilters.map((obj: any) =>
        obj?.type === FILTERS_MAPPING?.City ||
        obj?.type === FILTERS_MAPPING?.State ||
        obj?.type === FILTERS_MAPPING?.Country
          ? { ...obj, dimensions: [] } // update keys as needed
          : obj
      );
    } else {
      updatedArray = defaultSelectedFilters;
    }
    const payload = updatedArray.map((obj: any) => {
      if (obj?.type === code) {
        return {
          ...obj,
          dimensions: temp,
        };
      } else {
        return obj;
      }
    });

    if (
      (code === FILTERS_MAPPING?.Regions ||
        code === FILTERS_MAPPING.Country ||
        code === FILTERS_MAPPING.City ||
        code === FILTERS_MAPPING.State) &&
      viewPort === 'viewPortL'
    ) {
      setDefaultSelectedFilters(payload);
      setFilterButtonClicked('');
      setPopupOpenState(false);
      setRegionValue(payload[payload?.length - 1]?.dimensions[0]);
      setFiltersPayload(payload);

      payload?.forEach(({ type, dimensions }: { type: string; dimensions: string[] }) => {
        const paramKey = QUERY_PARAM_MAPPING[type];

        if (!paramKey) return;

        // Handle the specific brand length check
        if (type === 'BRANDS' && !isTempBrandsClicked && filterProps?.brandTags && filterProps?.brandTags?.length > 1)
          return;

        const hasMultipleBrands = filterProps?.brandTags?.length > 1;
        const shouldResetGeo = isGeoQuerySearch && isLocationSearch;

        updateURLParams(
          paramKey,
          dimensions,
          brandsClicked,
          hasMultipleBrands,
          shouldResetGeo ? false : isGeoQuerySearch,
          mapCenter
        );
      });

      if (isGeoQuerySearch && isLocationSearch) {
        setIsGeoQuerySearch(false);
      }

      /**
       * focuses back too the pill when the region/location get's selected.....
       */
      setTimeout(() => {
        getFocusOnFiltersPill();
      }, 1);
    } else {
      if (isGeoQuerySearch && isLocationSearch) {
        setIsGeoQuerySearch(false);
        getData(payload, true);
      } else {
        getData(payload);
      }
      setRegionValue(payload[payload?.length - 1]?.dimensions[0]);
      setDefaultSelectedFilters(payload);
    }
  };

  // show default selected filters on modal
  const showDefaultSelectedFilters = (filterData: any): any => {
    const result: any[] = [];

    if (!filterData?.buckets || !defaultSelectedFilters) {
      return result;
    }

    // Create a map for faster lookup of default filters by type
    const filterMap = new Map<string, string[]>();
    defaultSelectedFilters?.forEach((filter: { dimensions: string[]; type: string }) => {
      if (filter?.type === FILTERS_MAPPING?.Brands) {
        const hasBrandTags = !filterProps?.brandTags ? true : filterProps?.brandTags?.length > 0;
        if ((brandsClicked && hasBrandTags) || !hasBrandTags) {
          filterMap?.set(filter.type, filter.dimensions);
        }
      } else if (filter?.type !== FILTERS_MAPPING?.Brands && filter?.dimensions?.length) {
        filterMap?.set(filter?.type, filter?.dimensions);
      } else {
        filterMap?.set(filter?.type, []);
      }
    });

    filterData?.buckets?.forEach((data: any) => {
      const brandDimensions = filterMap?.get(FILTERS_MAPPING?.Brands);
      const activitiesDimensions = filterMap?.get(FILTERS_MAPPING?.Activities);

      // Handle brand filters & Activities Filter's
      if (brandDimensions && brandDimensions?.includes(data?.code)) {
        result?.push({
          brandTagId: data?.code,
          brandTagTitle: data?.label,
        });
      } else if (activitiesDimensions && activitiesDimensions?.includes(data?.code)) {
        result?.push({
          brandTagId: data?.code,
          brandTagTitle: data?.label,
        });
      } else {
        // Handle other filter types

        filterMap?.forEach((filterType, dimensions) => {
          /**
           * Checks the exact filter type and the dimensions....
           */
          const locationDimensions = ['BRANDS', 'STATES', 'CITIES', 'COUNTRIES'];

          if (filterType?.includes(data?.code)) {
            const isLocationDim = locationDimensions.includes(dimensions);

            if (!isLocationDim || dimensions?.includes(data?.enumCode)) {
              result.push({
                code: data?.code,
                label: data?.label,
              });
            }
          }
        });
      }
    });
    return result;
  };

  // to get all the brands in enabled and disabled state based on the UXL response

  const getCategorizedBrands = (
    currentFilterData: any,
    categorizedBrandsArray: any,
    filterName?: string // ➜ NEW parameter
  ) => {
    const updatedCategorizedArr = categorizedBrandsArray?.map((item: any) => {
      const updatedBrandListItem = item?.brandsLabelListItems?.map((brand: any) => {
        const matchingFacet = currentFilterData?.find((facet: any) => facet?.code === brand?.brandTagId);

        if (matchingFacet) {
          return { ...brand, ...matchingFacet, isDisabled: false };
        } else {
          return { ...brand, ...matchingFacet, isDisabled: true };
        }
      });

      // SORT only when filterName === "activities"
      const sortedBrands =
        filterName === FILTERS_MAPPING.Activities
          ? updatedBrandListItem.slice().sort((a: any, b: any) => {
              // Enabled first, disabled later
              return Number(a.isDisabled) - Number(b.isDisabled);
            })
          : updatedBrandListItem;

      return { ...item, brands: sortedBrands };
    });

    return updatedCategorizedArr;
  };

  // apply filters on click of apply button on modal
  const applyBtnHandler = () => {
    setFilterButtonClicked('');
    setRegionValue(defaultSelectedFilters[defaultSelectedFilters?.length - 1]?.dimensions[0]);
    setPopupOpenState(false);
    getFocusOnFiltersPill();

    filterProps?.brandTags && filterProps?.brandTags?.length > 1 && setIsTempBrandsClicked(brandsClicked);

    defaultSelectedFilters?.forEach(({ type, dimensions }: { type: string; dimensions: string[] }) => {
      const paramKey = QUERY_PARAM_MAPPING[type];

      const hasMultipleBrands = filterProps?.brandTags?.length > 1;

      // Handle the specific brand length check
      if (
        (type === 'BRANDS' &&
          hasMultipleBrands &&
          dimensions?.length !== filterProps?.brandTags?.length &&
          !brandsClicked) ||
        !paramKey
      )
        return;

      // Handle the specific brand length check

      updateURLParams(paramKey, dimensions, brandsClicked, hasMultipleBrands, isGeoQuerySearch, mapCenter);
    });

    setFiltersPayload(defaultSelectedFilters);
  };

  /**
   * CITIES + STATES + COUNTRIES facets merged for the Location `DropDownComponent`, with buckets removed when they
   * duplicate LEISURE_REGIONS options by code or label (see `excludeRegionBucketsFromLocationFacets`).
   */
  const combinedBuckets = useMemo(() => excludeRegionBucketsFromLocationFacets(filtersFacets), [filtersFacets]);

  const orderedCombinedBuckets = useMemo(() => {
    const normalizedRegion = regionValue?.toLowerCase?.() ?? '';
    const isStatesFirstRegion = REGIONS_WITH_STATES_FIRST.has(normalizedRegion);
    const order = isStatesFirstRegion
      ? [FILTERS_MAPPING.State, FILTERS_MAPPING.City]
      : REGIONS_WITH_COUNTRIES_FIRST.has(normalizedRegion)
      ? [FILTERS_MAPPING.Country, FILTERS_MAPPING.State, FILTERS_MAPPING.City]
      : [FILTERS_MAPPING.Country, FILTERS_MAPPING.State, FILTERS_MAPPING.City];

    const indexByType = new Map(order.map((facetType, index) => [facetType, index]));

    return [...combinedBuckets]
      .filter((facet: any) => !isStatesFirstRegion || facet?.type?.enumCode !== FILTERS_MAPPING.Country)
      .sort((a: any, b: any) => {
        const aType = a?.type?.enumCode;
        const bType = b?.type?.enumCode;
        const aIndex = indexByType.has(aType) ? (indexByType.get(aType) as number) : Number.MAX_SAFE_INTEGER;
        const bIndex = indexByType.has(bType) ? (indexByType.get(bType) as number) : Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      });
  }, [combinedBuckets, regionValue]);

  const componentMap = {
    LEISURE_REGIONS: (filterData: any) => (
      <DropDownComponent
        regions={[filterData]}
        defaultValue={regionValue}
        onClick={(region: { code: string }) => {
          handleFiltersSelectionChange(filterData?.type?.enumCode, region);
        }}
        clearButtonLabel={filterProps?.resetLabel}
        clearClickHandler={clearBtnHandler}
        viewPort={viewPort}
        textFieldLabel={isAuthorModeEnabled ? 'Regions' : filterProps?.regions?.regionsLabel}
        clickTrackingLoc={'Regions'}
        footerEnable={true}
        mapVariant={true}
      />
    ),
    BRANDS: (filterData: any) => (
      <BrandFilter
        title={viewPort !== 'viewPortL' && !isAuthorModeEnabled ? filterProps?.brands?.brandsLabel : 'Brands'}
        isHotelBrandSelectedFlag={true}
        brandCategories={getCategorizedBrands(filterData?.buckets, filterProps?.brands?.brandCategories)}
        defaultSelected={showDefaultSelectedFilters(filterData)}
        onChange={(obj): void => handleFiltersSelectionChange(filterData?.type?.enumCode, obj)}
        clickTrackingLoc={'Brand Filters'}
        className={filterProps?.brandTags?.length === 1 ? 'd-none' : ''}
        clickTrackingVal={`${filterData?.type?.enumCode} Section:`}
        mapVariant={true}
      />
    ),
    ACTIVITIES: (filterData: any) => (
      <BrandFilter
        title={
          viewPort !== 'viewPortL' && !isAuthorModeEnabled ? filterProps?.activities?.activitiesLabel : 'Activities'
        }
        isHotelBrandSelectedFlag={true}
        brandCategories={getCategorizedBrands(
          filterData?.buckets,
          mapActivitiesToBrandsStructure(filterProps?.activities, filterProps?.activityTags || []),
          FILTERS_MAPPING.Activities
        )}
        defaultSelected={showDefaultSelectedFilters(filterData)}
        onChange={(obj): void => handleFiltersSelectionChange(filterData?.type?.enumCode, obj)}
        clickTrackingLoc={'Activity Filters'}
        clickTrackingVal={`${filterData?.type?.enumCode} Section:`}
        isActivities={true}
        mapVariant={true}
      />
    ),
    AMENITIES: (filterData: any) => {
      /**
       * Checking for the avl. filters....
       */
      const AmenitiesDataAvl = filterAvailableFilters(
        filterData?.buckets,
        filterProps?.amenityTags || [],
        filterProps?.amenities?.amenitiesList || []
      );
      return (
        AmenitiesDataAvl?.length > 0 && (
          <CheckboxControlsList
            name={'amenities'}
            title={
              viewPort !== 'viewPortL' && !isAuthorModeEnabled ? filterProps?.amenities?.amenitiesLabel : 'Amenities'
            }
            controls={AmenitiesDataAvl}
            clickTrackingLoc={'amenities'}
            enableShowMoreToggle={true}
            onChange={(obj): void => handleFiltersSelectionChange(filterData?.type?.enumCode, obj)}
            showMoreCount={8}
            showSeparator={false}
            clickTrackingVal={`${filterData?.type?.enumCode} Section:`}
            defaultSelected={showDefaultSelectedFilters(filterData)}
            colLength={
              viewPort === 'viewPortL' ? MOBILE_COL_LENGTH : viewPort === 'viewPortM' ? COL_LENGTH : MOBILE_COL_LENGTH
            }
            luxuryVariant={true}
          />
        )
      );
    },
    PROPERTY_TYPES: (filterData: any) => {
      /**
       * Checking for the avl. filters....
       */
      const propTypeDataAvl = filterAvailableFilters(filterData?.buckets, filterProps?.propertyTypeTags || []);
      return (
        propTypeDataAvl?.length > 0 && (
          <CheckboxControlsList
            name={'property-types'}
            title={viewPort !== 'viewPortL' && !isAuthorModeEnabled ? filterProps?.propertyTypeLabel : 'Property'}
            controls={propTypeDataAvl}
            clickTrackingLoc={'property type'}
            enableShowMoreToggle={true}
            onChange={(obj): void => handleFiltersSelectionChange(filterData?.type?.enumCode, obj)}
            showMoreCount={8}
            showSeparator={false}
            clickTrackingVal={`${filterData?.type?.enumCode} Section:`}
            defaultSelected={showDefaultSelectedFilters(filterData)}
            colLength={
              viewPort === 'viewPortL' ? MOBILE_COL_LENGTH : viewPort === 'viewPortM' ? COL_LENGTH : MOBILE_COL_LENGTH
            }
            luxuryVariant={true}
          />
        )
      );
    },
    CITIES: () => (
      <DropDownComponent
        regions={orderedCombinedBuckets}
        defaultValue={
          defaultSelectedFilters[4]?.dimensions[0] ??
          defaultSelectedFilters[5]?.dimensions[0] ??
          defaultSelectedFilters[6]?.dimensions[0]
        }
        onClick={(region: { code: string }, type: { enumCode: string }) => {
          handleFiltersSelectionChange(type?.enumCode, region);
        }}
        viewPort={viewPort}
        textFieldLabel={isAuthorModeEnabled ? 'Location' : filterProps?.regions?.locationLabel}
        mapVariant={true}
      />
    ),
  };

  const showFiltersInSpecifiedOrder = (
    filtersOrderList: Array<{ filterName: string }>,
    filtersResponse: any[]
  ): JSX.Element | JSX.Element[] => {
    const responseMap = new Map(filtersResponse?.map(response => [response?.type?.enumCode, response]));

    const orderedAndMappedFilters = filtersOrderList?.flatMap(filter => {
      return filter?.filterName === FILTERS_MAPPING.Location ? LOCATION : filter;
    });

    const validFiltersToRender = orderedAndMappedFilters?.filter(filter => {
      const filterName = filter?.filterName;
      return filterName && responseMap?.has(filterName) && componentMap[filterName as keyof typeof componentMap];
    });

    if (validFiltersToRender?.length > 0) {
      return validFiltersToRender?.map((filter, index) => {
        const filterName = filter?.filterName;
        const response = responseMap?.get(filterName);
        const Component = componentMap[filterName as keyof typeof componentMap];

        return (
          <div key={index} className={filterName}>
            {Component(response)}
          </div>
        );
      });
    } else {
      return <div></div>;
    }
  };

  // reset the filter payload on modal
  const clearBtnHandler: any = (isReset = false) => {
    // reset the filter payload
    let payload = updateFiltersPayload(filterProps, isGeoQuerySearch, defaultRegion);

    if (filterButtonClicked === 'AllFilters' || filterButtonClicked === 'Regions' || filterButtonClicked === 'Brands') {
      setBrandsClicked(false);
    }

    if (isGeoQuerySearch && !isReset && filterButtonClicked === 'AllFilters') {
      payload = updateFiltersPayload(filterProps, true);
    } else if (
      isGeoQuerySearch &&
      (filterButtonClicked === 'Regions' || (isReset && filterButtonClicked === 'AllFilters'))
    ) {
      viewPort !== 'viewPortL' && setIsGeoQuerySearch(false);
      payload = updateFiltersPayload(filterProps, false, defaultRegion);
    } else if (filterButtonClicked !== 'Regions' && filterButtonClicked !== 'AllFilters') {
      payload = defaultSelectedFilters?.map((filter: { type: string }) => {
        if (filter.type === 'BRANDS' && filterButtonClicked === 'Brands') {
          return {
            type: 'BRANDS',
            dimensions: [...(filterProps?.brandTags || [])],
          };
        } else if (filter.type === 'AMENITIES' && filterButtonClicked === 'Amenities') {
          return {
            type: 'AMENITIES',
            dimensions: [...(filterProps?.amenityTags || [])],
          };
        } else if (filter.type === 'PROPERTY_TYPES' && filterButtonClicked === 'Property') {
          return {
            type: 'PROPERTY_TYPES',
            dimensions: [...(filterProps?.propertyTypeTags || [])],
          };
        } else if (filter.type === 'ACTIVITIES' && filterButtonClicked === 'Activities') {
          return {
            type: 'ACTIVITIES',
            dimensions: [...(filterProps?.activityTags || [])],
          };
        } else {
          return filter;
        }
      });
    }

    if (isGeoQuerySearch && (filterButtonClicked === 'Regions' || (isReset && filterButtonClicked === 'AllFilters'))) {
      getData(payload, true);
    } else {
      isLocationSearch ? getData(payload, true) : getData(payload);
    }

    isReset ? setRegionValue('') : setRegionValue(payload?.[payload?.length - 1]?.dimensions[0]);

    setDefaultSelectedFilters(payload);
  };

  // reset the filters payload to it's previous state on close of modal
  const resetFiltersPayloadOnClose = () => {
    prevfiltersRef?.current &&
      setRegionValue(prevfiltersRef?.current[prevfiltersRef?.current?.length - 1]?.dimensions[0]);

    getData(prevfiltersRef?.current ?? []);
  };

  // reset the filters payload to it's previous state on click outside of modal
  const resetFiltersPayloadOnClickOutside = () => {
    setBrandsClicked(isTempBrandsClicked);
    setRegionValue(prevfiltersRefVal[prevfiltersRefVal?.length - 1]?.dimensions[0]);

    getData(prevfiltersRefVal);
  };

  const getModalData = () => {
    const filtersMap: any = FILTERS_MAPPING;
    const filterName = filtersMap?.[filterButtonClicked];

    if (filterButtonClicked === 'AllFilters') {
      const isAuthorMode = filterProps?.isAuthorMode ?? false;
      const filtersOrderList =
        filterProps?.filtersOrderList?.length > 0
          ? getFilteredOrder(filterProps, filterProps?.filtersOrderList, isAuthorMode)
          : getFilteredOrder(filterProps, DEFAULT_FILTERS_ORDER, isAuthorMode);

      return showFiltersInSpecifiedOrder(filtersOrderList, filtersFacets);
    } else if (filterName) {
      const data = showFiltersInSpecifiedOrder(
        [
          {
            filterName,
          },
        ],
        filtersFacets
      );
      return data;
    } else {
      return <div></div>;
    }
  };

  useEffect(() => {
    const MAPPINGS: any = FILTERS_MAPPING;
    const pillElement = document?.getElementById(MAPPINGS[filterButtonClicked]);
    const pillRect = pillElement?.getBoundingClientRect();

    if (viewPort === 'viewPortL' && filterPanelRef.current && pillRect && filterButtonClicked) {
      filterPanelRef.current.style.position = 'fixed';

      filterPanelRef.current.style.top = `${
        pillRect.bottom + (filterButtonClicked === 'Regions' || filterButtonClicked === 'Location' ? -16 : 0)
      }px`;
      filterPanelRef.current.style.left = `${pillRect.left}px`;
      filterPanelRef.current.style.zIndex = 1000;
      filterPanelRef.current.focus();
    }

    const handleScroll = () => {
      /**
       * condition to check the popup is opened or not in desktop view
       */
      if (popupOpenState === true && viewPort === 'viewPortL') {
        resetFiltersPayloadOnClickOutside();
        setPopupOpenState(false);
        setFilterButtonClicked('');
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [filterButtonClicked, viewPort]);

  const getFocusOnFiltersPill = () => {
    if (filterButtonClicked === 'Regions') {
      const input = document.getElementById('LEISURE_REGIONS');
      if (input) {
        input.focus();
      }
    } else if (filterButtonClicked === 'Location') {
      const input = document.getElementById('LOCATION');
      if (input) {
        input.focus();
      }
    } else if (filterButtonClicked === 'Activities') {
      const input = document.getElementById('ACTIVITIES');
      if (input) {
        input.focus();
      }
    } else if (filterButtonClicked === 'Property') {
      const input = document.getElementById('PROPERTY_TYPES');
      if (input) {
        input.focus();
      }
    } else if (filterButtonClicked === 'Amenities') {
      const input = document.getElementById('AMENITIES');
      if (input) {
        input.focus();
      }
    } else if (filterButtonClicked === 'Brands') {
      const input = document.getElementById('BRANDS');
      if (input) {
        input.focus();
      }
    }
  };

  return (
    <StyledSearchResultsFilterPanel
      data-testid="luxury-SearchResultsFilterPanel"
      data-component-name="m-luxury-SearchResultsFilterPanel"
      ref={filterPanelRef}
      role="dialog"
      aria-label="search results filter panel"
      tabIndex={viewPort === 'viewPortL' ? -1 : 0}
      onBlur={e => {
        if (
          !dropdownRef.current?.contains(e?.relatedTarget) &&
          !e?.relatedTarget?.classList?.contains('pill') &&
          viewPort === 'viewPortL'
        ) {
          resetFiltersPayloadOnClickOutside();
          setPopupOpenState(false);
          setFilterButtonClicked('');
          getFocusOnFiltersPill();
        }
      }}
    >
      {viewPort !== 'viewPortL' ? (
        <Modal
          className={clsx('custom-scrollbar', 'popup-header-container', popupOpenState ? '' : 'close-popup-header')}
          show={true}
          handleBlur={true}
          popupOpenState={popupOpenState}
          disableScrollOnBody={true}
          setPopupOpenState={setPopupOpenState}
          allFilterPopUp={true}
          role="dialog"
          aria-labelledby={'filtersHeaderLabel'}
          eventClass="radio-input checkbox-input subtext region-el text-container icon-container region-text m-icon-text-block"
          onClose={(): void => {
            resetFiltersPayloadOnClickOutside();
            setPopupOpenState(false);
            setFilterButtonClicked('');
          }}
          modalId={'filtersHeaderLabel'}
          aria-label="Search Results Filters Modal"
        >
          <Modal.Header
            className={clsx('modal-header px-3 py-3')}
            customHeadingClass={'t-subtitle-l'}
            labelText={filterProps?.filterLabel}
            popupHeaderOnCLoseFunc={(): void => {
              resetFiltersPayloadOnClose();
              setPopupOpenState(false);
              setFilterButtonClicked('');
            }}
          />

          <Modal.Body className={clsx('all-filters', 'custom-scrollbar', 'px-3 py-3', 'filters-modal-body')}>
            {getModalData()}
          </Modal.Body>

          <Modal.Footer
            clearClickHandler={() => clearBtnHandler(false)}
            setPopUpOpenState={setPopupOpenState}
            applyBtnClickHandler={applyBtnHandler}
            clearButtonLabel={filterProps?.clearFiltersLabel}
            applyLabel={filterProps?.doneLabel}
            applyButtonDisabled={false}
            className={clsx('modal-footer px-3 py-3')}
            clickTrackingLoc={'All Filters'}
            clearBtnClassname={'m-link modal-clear-btn px-0'}
            resetButtonLabel={filterProps?.resetMapLabel}
            resetBtnClassname="m-link mr-5 px-0"
            resetBtnClickTrackValue={''}
            resetClickHandler={() => clearBtnHandler(true)}
          />
        </Modal>
      ) : (
        <>
          {filterButtonClicked && (
            <div className="filter-dropdown color-scheme1">
              {filterButtonClicked === 'Regions' || filterButtonClicked === 'Location' ? (
                <div className="region-filter" ref={dropdownRef} data-testid="luxuryMap-regions-filter">
                  {getModalData()}
                </div>
              ) : (
                <div ref={dropdownRef}>
                  <DropDownModal
                    dropDownChildrenClassName="col-12 px-1"
                    isAutoScrollEnabled={true}
                    dropDownSpecificFlag={false}
                    scrollDownModalCloseState={false}
                    labelledBy="luxury-dropdown"
                    show={true}
                    className="dropdown-container custom-map-dropdown"
                    childrenClassName="luxury-dropdown-main"
                    dropdownModalOpenState={popupOpenState}
                    setDropdownModalOpenState={setPopupOpenState}
                    applyBtnClickHandler={applyBtnHandler}
                    applyLabel={filterProps?.doneLabel}
                    clearButtonLabel={filterProps?.resetLabel}
                    desktopDropdown={true}
                    mobileFooterEnabled={true}
                    clearClickHandler={() => clearBtnHandler(false)}
                    clickTrackingLoc={filterButtonClicked}
                  >
                    <div className="filter-popup mt-3">{getModalData()}</div>
                  </DropDownModal>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </StyledSearchResultsFilterPanel>
  );
};
